import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ShoppingCart, User, Menu, X, ChevronDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Header = ({ cartCount = 0 }) => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileOpenCategories, setMobileOpenCategories] = useState([]); // Track which categories are open in mobile
  const [settings, setSettings] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Search states
  const [searchQuery, setSearchQuery] = useState('');
  const [searchSuggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userData, setUserData] = useState(null);
  const userMenuRef = React.useRef(null);

  // Toggle mobile category
  const toggleMobileCategory = (categoryId) => {
    setMobileOpenCategories(prev => 
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const settingsRes = await fetch(`${API_URL}/api/settings`);
        const settingsData = await settingsRes.json();
        setSettings(settingsData);

        const categoriesRes = await fetch(`${API_URL}/api/categories`);
        const categoriesData = await categoriesRes.json();
        setCategories(categoriesData);
      } catch (error) {
        console.error('Header veri yükleme hatası:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Autocomplete search
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (searchQuery.length < 2) {
        setSuggestions([]);
        return;
      }

      setSearchLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/search/autocomplete?q=${encodeURIComponent(searchQuery)}`);
        const data = await response.json();
        setSuggestions(data);
        setShowSuggestions(true);
      } catch (error) {
        console.error('Autocomplete hatası:', error);
      } finally {
        setSearchLoading(false);
      }
    };

    const debounceTimer = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setShowSuggestions(false);
      setSearchQuery('');
    }
  };

  const handleSuggestionClick = (productId) => {
    navigate(`/product/${productId}`);
    setShowSuggestions(false);
    setSearchQuery('');
  };

  // Check login status
  useEffect(() => {
    const checkLoginStatus = () => {
      const token = localStorage.getItem('userToken');
      const user = localStorage.getItem('user');
      
      if (token && user) {
        setIsLoggedIn(true);
        try {
          setUserData(JSON.parse(user));
        } catch (e) {
          console.error('Error parsing user data:', e);
          setUserData(null);
        }
      } else {
        setIsLoggedIn(false);
        setUserData(null);
      }
    };
    
    checkLoginStatus();
    
    // Listen for custom login event (same tab)
    window.addEventListener('loginStateChanged', checkLoginStatus);
    // Listen for storage changes (other tabs)
    window.addEventListener('storage', checkLoginStatus);
    
    return () => {
      window.removeEventListener('loginStateChanged', checkLoginStatus);
      window.removeEventListener('storage', checkLoginStatus);
    };
  }, []);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu]);

  const handleLogout = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API_URL}/api/auth/logout`, { method: 'POST' });
    } catch (_) { /* ignore */ }
    localStorage.removeItem('userToken');
    localStorage.removeItem('user');
    window.dispatchEvent(new Event('loginStateChanged')); // Notify Header
    setIsLoggedIn(false);
    setUserData(null);
    setShowUserMenu(false);
    navigate('/');
  };

  if (loading || !settings) {
    return (
      <header className="sticky top-0 z-50 bg-white shadow-sm">
        <div className="h-8 bg-black"></div>
        <div className="h-20 bg-white"></div>
        <div className="h-12 bg-black"></div>
      </header>
    );
  }

  const { headerSettings } = settings;
  const enabledAnnouncements = headerSettings.announcementBar?.items?.filter(item => item.enabled) || [];

  return (
    <header className="sticky top-0 z-50 bg-white shadow-sm">
      {/* Top announcement bar */}
      {headerSettings.announcementBar?.enabled && enabledAnnouncements.length > 0 && (
        <div className="bg-black text-white text-sm py-2">
          <div className="container mx-auto px-4 flex items-center justify-center gap-4 flex-wrap text-center">
            {enabledAnnouncements.map((item, index) => (
              <React.Fragment key={index}>
                {index > 0 && <span className="hidden sm:inline">|</span>}
                <span>{item.text}</span>
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* Main header */}
      <div className="border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-4">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0">
              {headerSettings.logo?.image ? (
                <img src={headerSettings.logo.image} alt={headerSettings.logo.text} className="h-12 object-contain" />
              ) : (
                <div className="bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-3 font-bold text-xl transform -skew-x-12">
                  <span className="inline-block transform skew-x-12">⚡ {headerSettings.logo?.text || 'Cep Premium'}</span>
                </div>
              )}
            </Link>

            {/* Search bar - Desktop only (1024px+) */}
            <div className="hidden lg:flex flex-1 max-w-md relative">
              <form onSubmit={handleSearch} className="w-full">
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => searchSuggestions.length > 0 && setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                    placeholder="Ürün ara..."
                    className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                  />
                  <button type="submit" className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-600 transition-colors">
                    <Search size={20} />
                  </button>
                </div>
              </form>

              {/* Autocomplete Suggestions */}
              {showSuggestions && searchSuggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-96 overflow-y-auto z-50">
                  {searchSuggestions.map((product) => (
                    <div
                      key={product.id}
                      onClick={() => handleSuggestionClick(product.id)}
                      className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-0"
                    >
                      <img 
                        src={product.image} 
                        alt={product.name}
                        className="w-12 h-12 object-cover rounded border-2 border-gray-300 shadow-sm"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-800 line-clamp-1">{product.name}</p>
                        <p className="text-sm text-red-600 font-semibold">{product.price.toLocaleString('tr-TR')} TL</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Right icons */}
            <div className="flex items-center gap-4">
              <button
                className="lg:hidden text-gray-600 hover:text-red-600 transition-colors"
                onClick={() => setSearchOpen(!searchOpen)}
              >
                <Search size={24} />
              </button>
              
              {/* User Account with Dropdown */}
              <div className="relative" ref={userMenuRef}>
                {isLoggedIn ? (
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center gap-2 text-gray-600 hover:text-red-600 transition-colors"
                  >
                    <User size={24} />
                    <span className="hidden xl:block text-sm">
                      {userData?.first_name || 'Hesabım'}
                    </span>
                    <ChevronDown size={16} className="hidden xl:block" />
                  </button>
                ) : (
                  <Link 
                    to="/login" 
                    className="text-gray-600 hover:text-red-600 transition-colors flex items-center gap-1"
                    title="Giriş Yap"
                  >
                    <User size={24} />
                    <span className="hidden xl:block text-sm">Giriş</span>
                  </Link>
                )}

                {/* Dropdown Menu */}
                {isLoggedIn && showUserMenu && (
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                    <div className="px-4 py-3 border-b border-gray-200">
                      <p className="font-semibold text-gray-900">
                        {userData?.first_name} {userData?.last_name}
                      </p>
                      <p className="text-sm text-gray-600">{userData?.email}</p>
                    </div>
                    
                    <Link
                      to="/profile"
                      onClick={() => setShowUserMenu(false)}
                      className="block px-4 py-2 text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors"
                    >
                      👤 Hesap Bilgilerim
                    </Link>
                    
                    <Link
                      to="/profile"
                      onClick={() => {
                        setShowUserMenu(false);
                        // Navigate to orders tab
                        setTimeout(() => {
                          const ordersTab = document.querySelector('[data-tab="orders"]');
                          if (ordersTab) ordersTab.click();
                        }, 100);
                      }}
                      className="block px-4 py-2 text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors"
                    >
                      📦 Siparişlerim
                    </Link>
                    
                    <Link
                      to="/profile"
                      onClick={() => {
                        setShowUserMenu(false);
                        setTimeout(() => {
                          const addressesTab = document.querySelector('[data-tab="addresses"]');
                          if (addressesTab) addressesTab.click();
                        }, 100);
                      }}
                      className="block px-4 py-2 text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors"
                    >
                      📍 Adreslerim
                    </Link>
                    
                    <div className="border-t border-gray-200 mt-2 pt-2">
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-red-600 hover:bg-red-50 transition-colors font-medium"
                      >
                        🚪 Çıkış Yap
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              <Link to="/cart" className="relative text-gray-600 hover:text-red-600 transition-colors">
                <ShoppingCart size={24} />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                    {cartCount}
                  </span>
                )}
              </Link>

              <button
                className="lg:hidden text-gray-600 hover:text-red-600 transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>

          {/* Mobile/Tablet search */}
          {searchOpen && (
            <div className="lg:hidden mt-4 relative">
              <form onSubmit={handleSearch}>
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => searchSuggestions.length > 0 && setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                    placeholder="Ürün ara..."
                    className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                  />
                  <button type="submit" className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400">
                    <Search size={20} />
                  </button>
                </div>
              </form>

              {/* Mobile Autocomplete Suggestions */}
              {showSuggestions && searchSuggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-96 overflow-y-auto z-50">
                  {searchSuggestions.map((product) => (
                    <div
                      key={product.id}
                      onClick={() => handleSuggestionClick(product.id)}
                      className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-0"
                    >
                      <img 
                        src={product.image} 
                        alt={product.name}
                        className="w-12 h-12 object-cover rounded border-2 border-gray-300 shadow-sm"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-800 line-clamp-1">{product.name}</p>
                        <p className="text-sm text-red-600 font-semibold">{product.price.toLocaleString('tr-TR')} TL</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="bg-black text-white">
        <div className="container mx-auto px-4">
          {/* Desktop nav (1024px+) - 2 rows with flex-wrap */}
          <ul className="hidden lg:flex items-center justify-center gap-x-2 gap-y-1 py-2 flex-wrap">
            {categories.filter(cat => cat.enabled).map((category) => {
              const hasSubmenu = category.subcategories && category.subcategories.length > 0;
              const enabledSubmenu = hasSubmenu ? category.subcategories.filter(sub => sub.enabled) : [];
              
              return (
                <li
                  key={category.id}
                  className="relative group"
                  onMouseEnter={() => hasSubmenu && enabledSubmenu.length > 0 && setActiveDropdown(category.id)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <Link
                    to={`/category/${category.slug}`}
                    className="flex items-center gap-1 px-3 py-2 hover:bg-red-600 rounded transition-colors whitespace-nowrap text-sm font-medium"
                  >
                    {category.name}
                    {hasSubmenu && enabledSubmenu.length > 0 && <ChevronDown size={16} />}
                  </Link>
                  
                  {hasSubmenu && enabledSubmenu.length > 0 && activeDropdown === category.id && (
                    <div className="absolute top-full left-0 bg-white text-black shadow-lg rounded-lg py-2 min-w-[220px] mt-0 z-50">
                      {enabledSubmenu.map((subItem) => (
                        <Link
                          key={subItem.id}
                          to={`/category/${subItem.slug}`}
                          className="block px-4 py-2 hover:bg-red-50 hover:text-red-600 transition-colors text-sm"
                        >
                          {subItem.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

          {/* Mobile/Tablet nav with scroll and accordion */}
          {mobileMenuOpen && (
            <div className="lg:hidden py-4 max-h-[60vh] overflow-y-auto">
              <ul className="space-y-1">
                {categories.filter(cat => cat.enabled).map((category) => {
                  const hasSubmenu = category.subcategories && category.subcategories.length > 0;
                  const enabledSubmenu = hasSubmenu ? category.subcategories.filter(sub => sub.enabled) : [];
                  const isOpen = mobileOpenCategories.includes(category.id);
                  
                  return (
                    <li key={category.id}>
                      <div className="flex items-center justify-between">
                        <Link
                          to={`/category/${category.slug}`}
                          className="flex-1 py-2 px-2 hover:bg-red-600 rounded transition-colors"
                          onClick={() => !hasSubmenu && setMobileMenuOpen(false)}
                        >
                          {category.name}
                        </Link>
                        {hasSubmenu && enabledSubmenu.length > 0 && (
                          <button
                            onClick={() => toggleMobileCategory(category.id)}
                            className="p-2 hover:bg-red-600 rounded transition-colors"
                            aria-label={isOpen ? 'Kapat' : 'Aç'}
                          >
                            <ChevronDown 
                              size={18} 
                              className={`transform transition-transform ${isOpen ? 'rotate-180' : ''}`}
                            />
                          </button>
                        )}
                      </div>
                      {hasSubmenu && enabledSubmenu.length > 0 && isOpen && (
                        <ul className="pl-4 mt-1 space-y-1 border-l-2 border-red-600">
                          {enabledSubmenu.map((subItem) => (
                            <li key={subItem.id}>
                              <Link
                                to={`/category/${subItem.slug}`}
                                className="block py-1.5 px-2 text-sm text-gray-300 hover:bg-red-600 rounded transition-colors"
                                onClick={() => setMobileMenuOpen(false)}
                              >
                                {subItem.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </nav>
    </header>
  );
};

export default Header;
